import axios from 'axios'

/**
 * FarmavelAPI Class
 * Maneja todas las comunicaciones REST con el nuevo backend de Farmavel (Laravel).
 * Implementa autenticación Sanctum mediante tokens Bearer.
 */
class FarmavelAPI {
    constructor() {
        this.baseUrl = this._determineBaseUrl()

        this.client = axios.create({
            baseURL: this.baseUrl,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        })

        // Interceptor para incluir el token de Sanctum en cada petición
        this.client.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('farmavel_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => Promise.reject(error)
        )
    }

    getBaseUrl() {
        return this.baseUrl
    }

    /**
     * Determina la URL base según el hostname actual para el nuevo backend Laravel.
     * @private
     */
    _determineBaseUrl() {
        const { hostname } = window.location

        // URLs del nuevo backend Laravel (bajo el prefijo /api).
        //
        // ⚠️ En local DEBE ser admin2.farmavel.test: es el proyecto farmavel-newadmin
        // (la remodelación) y usa la base de datos `farmaval`. El otro sitio Herd,
        // farmavel.test, es el proyecto `farmavel` de la versión actual y usa
        // `farmaval_v1`, con el esquema de productos anterior a la unificación de
        // idiomas: apuntar ahí devuelve productos duplicados y sin traducciones.
        //
        // Tiene que ser https: por http el sitio responde 301, y un 301 degrada el
        // POST del login a GET, así que el token nunca se emite.
        const envs = {
            local: 'http://farmavel.test/api/',
            staging: 'https://farmavel.on-forge.com/api/',
            production: 'https://farmaval.farmaval.net/api/',
        }

        if (hostname === 'localhost' || hostname.includes('.test')) {
            return envs.local
        }

        if (hostname.includes('staging')) {
            return envs.staging
        }

        return envs.production
    }

    /**
     * Manejador central de peticiones.
     * @private
     */
    async _request(method, path, data = null, config = {}) {
        try {
            const requestConfig = {
                method,
                url: path,
                ...config,
            }

            if (data) {
                if (method.toLowerCase() === 'get') {
                    requestConfig.params = data
                } else {
                    requestConfig.data = data
                }
            }

            const response = await this.client.request(requestConfig)
            return response.data
        } catch (error) {
            return this._handleError(error)
        }
    }

    /**
     * Manejo centralizado de errores.
     * @private
     */
    _handleError(error) {
        const errorDetail = {
            message:
                error.response?.data?.message || // Laravel suele usar 'message' en lugar de 'msg'
                error.message ||
                'Unknown API Error',
            status: error.response?.status,
            data: error.response?.data,
        }

        // Si es un error de autenticación (401), podríamos limpiar el token
        if (error.response?.status === 401) {
            localStorage.removeItem('farmavel_token')
        }

        console.error('[FarmavelAPI Error]:', errorDetail)
        throw errorDetail
    }

    // Métodos REST Públicos

    get(path, params = {}, config = {}) {
        return this._request('get', path, params, config)
    }

    post(path, data = {}, config = {}) {
        return this._request('post', path, data, config)
    }

    /**
     * POST multipart/form-data (para subida de archivos).
     * Elimina el Content-Type por defecto para que axios lo calcule con el boundary correcto.
     */
    postForm(path, formData) {
        return this.client.post(path, formData, {
            headers: { 'Content-Type': undefined },
        })
            .then(r => r.data)
            .catch(e => this._handleError(e))
    }

    put(path, data = {}, config = {}) {
        return this._request('put', path, data, config)
    }

    patch(path, data = {}, config = {}) {
        return this._request('patch', path, data, config)
    }

    delete(path, config = {}) {
        return this._request('delete', path, null, config)
    }

    /**
     * POST multipart/form-data (subida de archivos). El Content-Type se deja
     * en `undefined` para que el navegador calcule el boundary automáticamente;
     * si se fijara manualmente a 'multipart/form-data' sin boundary, el
     * request llegaría roto al backend.
     */
    postForm(path, formData, config = {}) {
        return this._request('post', path, formData, {
            ...config,
            headers: { ...(config.headers || {}), 'Content-Type': undefined },
        })
    }
}

// Exportar instancia única
export const farmavel = new FarmavelAPI()
